// Impact section — reframed for Fall + Winter 2026.
// Previously showed placeholder counters (0+ / 2,500+). Per client direction,
// we now surface the *reflective* measures of community impact until finalized
// verified numbers are ready. Structure keeps the component name stable so
// existing routing and CMS references keep working.

import { Heart, Sparkles, HandHeart, UsersRound, HomeIcon } from 'lucide-react';
import { usePreviewValue } from '../contexts/CMSPreviewContext';
import { useI18n } from '../contexts/I18nContext';

const QUESTIONS = [
  { key: 'impact.q1', icon: UsersRound, accent: '#E5614C' },
  { key: 'impact.q2', icon: Heart, accent: '#5DCAB5' },
  { key: 'impact.q3', icon: HomeIcon, accent: '#FCD968' },
  { key: 'impact.q4', icon: Sparkles, accent: '#E5614C' },
  { key: 'impact.q5', icon: HandHeart, accent: '#5DCAB5' },
];

export default function ImpactStats() {
  const { t } = useI18n();
  const eyebrow = usePreviewValue('impact', 'eyebrow', t('impact.eyebrow'));
  const h1 = usePreviewValue('impact', 'headline_1', t('impact.title1'));
  const h2 = usePreviewValue('impact', 'headline_2', t('impact.title2'));
  const subtitle = usePreviewValue('impact', 'subtitle', t('impact.subtitle'));
  const close = usePreviewValue('impact', 'close', t('impact.close'));

  return (
    <section className="py-20 lg:py-28 bg-[#FAF3EE] relative overflow-hidden" data-testid="impact-community">
      <div className="absolute top-16 -left-12 w-72 h-72 rounded-full bg-[#5DCAB5]/15 blur-3xl pointer-events-none" />
      <div className="absolute bottom-16 -right-12 w-80 h-80 rounded-full bg-[#E5614C]/15 blur-3xl pointer-events-none" />

      <div className="max-w-5xl mx-auto px-5 md:px-10 relative">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 text-[#E5614C] font-semibold uppercase tracking-[0.18em] text-[12px]">
            <span className="h-[2px] w-8 bg-[#E5614C]" /> {eyebrow}
          </div>
          <h2 className="font-serif-display text-[36px] sm:text-[44px] lg:text-[52px] leading-[1.05] font-bold text-[#2A1B1A] mt-4">
            {h1} <span className="italic text-[#E5614C]">{h2}</span>
          </h2>
          <p className="mt-5 text-[#2A1B1A]/70 text-[16px] leading-relaxed">
            {subtitle}
          </p>
        </div>

        <ul className="mt-12 grid gap-3 sm:gap-4 max-w-3xl mx-auto">
          {QUESTIONS.map((q) => {
            const Icon = q.icon;
            return (
              <li
                key={q.key}
                className="group flex items-start gap-4 sm:gap-5 rounded-2xl bg-white border border-[#2A1B1A]/8 p-5 sm:p-6 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-20px_rgba(42,27,26,0.25)] hover:border-transparent transition-[transform,box-shadow,border-color] duration-300"
                data-testid={`impact-question-${q.key.split('.').pop()}`}
              >
                <div
                  className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300"
                  style={{ backgroundColor: `${q.accent}1a`, color: q.accent }}
                >
                  <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
                </div>
                <div className="pt-2 sm:pt-2.5 text-[16px] sm:text-[18px] font-medium text-[#2A1B1A] leading-snug">
                  {t(q.key)}
                </div>
              </li>
            );
          })}
        </ul>

        <p className="mt-10 text-center font-serif-display italic text-[20px] sm:text-[22px] text-[#2A1B1A]/85">
          {close}
        </p>
      </div>
    </section>
  );
}
